import { ReputationBadge, StarRating } from './Stars';
import { useReviews } from './use-reviews';
import { relativeTime } from '@/lib/relative-time';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * The reviews written about the signed-in account. [Parent step 21]
 *
 * Shown on the profile because that is where someone goes to see how they are
 * represented — and because the score other people see should not be hidden
 * from the person it describes.
 */
export function MyReviews() {
  const reviews = useReviews({ pageSize: 20 });

  const items = reviews.data?.items ?? [];
  const count = reviews.data?.total ?? 0;
  const average =
    items.length > 0
      ? Math.round((items.reduce((sum, review) => sum + review.rating, 0) / items.length) * 10) / 10
      : null;

  return (
    <section className="rounded-2xl border border-line bg-white shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <h2 className="text-sm font-bold text-ink">Reviews about you</h2>
        <ReputationBadge
          reputation={{ average, count, distribution: [0, 0, 0, 0, 0] }}
          size="md"
        />
      </div>

      {reviews.isPending && (
        <div className="space-y-3 p-5" aria-busy="true">
          <Skeleton className="h-14" radius="md" />
        </div>
      )}

      {reviews.data && items.length === 0 && (
        <p className="px-5 py-8 text-center text-sm text-slate-500">
          No reviews yet. They arrive once you finish a job and the work is approved.
        </p>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-line">
          {items.map((review) => (
            <li key={review.id} className="px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <StarRating value={review.rating} />
                  <span className="text-sm font-semibold text-ink">{review.authorName}</span>
                </div>
                <span className="text-xs text-slate-400">{relativeTime(review.createdAt)}</span>
              </div>
              <p className="mt-1 text-xs font-medium text-slate-500">{review.jobTitle}</p>
              {review.comment && (
                <p className="mt-2 text-sm leading-relaxed text-slate-600">“{review.comment}”</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
